import PropTypes from 'prop-types';
import React from 'react';
import { AlertTriangle, Badge, Button, Card, CheckCircle } from '@opendate-app/setlist';

import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';

const USDollar = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (value) => {
  if (!value || Number.isNaN(Date.parse(value))) return null;
  const date = offsetDateForBrowser(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const formatDate = (date) => date.toLocaleDateString('en-us', { year: "numeric", month: "short", day: "numeric" });

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const dueDateFor = (deposit) => (
  parseDate(deposit.due_date || (deposit.due_date_type === "specific" ? deposit.specific_due_date : null))
);

const daysOverdue = (dueDate) => Math.round((startOfToday() - dueDate) / DAY_MS);

export const depositStatus = (deposit) => {
  if (deposit.paid) return "paid";
  const dueDate = dueDateFor(deposit);
  return dueDate && daysOverdue(dueDate) >= 1 ? "overdue" : "due";
};

const relativeDueText = (deposit) => (
  deposit.due_date_type === "relative" && deposit.relative_due_date != null
    ? `${deposit.relative_due_date} days before show`
    : null
);

const dueLine = (deposit) => {
  const dueDate = dueDateFor(deposit);
  const relative = relativeDueText(deposit);
  if (dueDate) return [`Due ${formatDate(dueDate)}`, relative].filter(Boolean).join(" · ");
  return relative ? `Due ${relative}` : "—";
};

const paidLine = (deposit) => {
  const paidAt = parseDate(deposit.paid_at);
  return [paidAt && formatDate(paidAt), deposit.paid_by && `by ${deposit.paid_by}`].filter(Boolean).join(" ");
};

const typeLabel = (deposit) => {
  const type = deposit.deposit_type || "";
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} deposit`;
};

const StatusLine = ({ deposit, status }) => {
  if (status === "paid") {
    return (
      <>
        <Badge size="sm" color="success" icon={CheckCircle}>Paid</Badge>
        <span style={{ color: 'var(--sl-text-tertiary)' }}>{paidLine(deposit)}</span>
      </>
    );
  }

  if (status === "overdue") {
    const days = daysOverdue(dueDateFor(deposit));
    return (
      <>
        <Badge size="sm" color="orange" icon={AlertTriangle}>Overdue {days} {days === 1 ? "day" : "days"}</Badge>
        <span style={{ color: 'var(--sl-text-warning)' }}>Was due {formatDate(dueDateFor(deposit))}</span>
      </>
    );
  }

  return (
    <>
      <Badge size="sm" color="blue" dot>Unpaid</Badge>
      <span style={{ color: 'var(--sl-text-tertiary)' }}>{dueLine(deposit)}</span>
    </>
  );
};

StatusLine.propTypes = {
  deposit: PropTypes.object.isRequired,
  status: PropTypes.oneOf(["paid", "overdue", "due"]).isRequired
};

const overdueCardStyle = {
  background: 'var(--sl-bg-warning-primary)',
  borderColor: 'var(--sl-border-warning)'
};

const DepositCard = ({ name, deposit, columnClass, canManage, onTogglePaid }) => {
  const status = depositStatus(deposit);

  return (
    <div className={columnClass}>
      <Card padding="md" style={status === "overdue" ? overdueCardStyle : undefined}>
        <div className="d-flex justify-content-between align-items-center">
        <div style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '10px' }}>
            <StatusLine deposit={deposit} status={status} />
          </div>
          <div className="d-flex align-items-baseline flex-wrap" style={{ gap: '12px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: 'var(--sl-text-primary)' }}>
              {deposit.amount == null ? "—" : USDollar.format(deposit.amount)}
            </span>
            <span style={{ fontSize: '18px', color: 'var(--sl-text-secondary)' }}>
              {name} · {typeLabel(deposit)}
            </span>
          </div>
        </div>
        {canManage ? (
          <Button variant={deposit.paid ? "secondary" : "primary"} onClick={onTogglePaid}>
            {deposit.paid ? "Mark unpaid" : "Mark paid"}
          </Button>
        ) : null}
        </div>
      </Card>
    </div>
  );
};

DepositCard.propTypes = {
  name: PropTypes.string,
  deposit: PropTypes.object.isRequired,
  columnClass: PropTypes.string,
  canManage: PropTypes.bool,
  onTogglePaid: PropTypes.func.isRequired
};

DepositCard.defaultProps = {
  columnClass: "col-12 mb-2"
};

export default DepositCard;
